<script setup lang="ts">
import { faCheck, faChevronDown } from '@fortawesome/pro-light-svg-icons'
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
  Label,
} from 'reka-ui'

withDefaults(
  defineProps<{
    placeholder: string
    label?: string
    id?: string
    name?: string
    intent?: 'filled' | 'outlined'
  }>(),
  {
    intent: 'filled',
  },
)
const selectedSortOrder = defineModel<{ value: string, label: string } | undefined>({ default: undefined })
const isSelected = computed(() => !!selectedSortOrder?.value)
</script>

<template>
  <ComboboxRoot
    v-slot="{ open }"
    v-model="selectedSortOrder"
    class="relative"
  >
    <Label :for="id" class="sr-only">{{ label }}</Label>
    <ComboboxAnchor class="px-s" :class="comboboxVariants.anchor.base({ open, selected: isSelected, intent })">
      <ComboboxTrigger :class="comboboxVariants.anchor.trigger({ selected: isSelected, intent })" class="flex items-center w-full gap-xs">
        <ComboboxInput
          :id="id"
          :placeholder="placeholder"
          :class="comboboxVariants.anchor.input({ selected: isSelected, intent })"
          readonly
          :value="selectedSortOrder?.label || ''"
        />
        <Icon
          :icon="faChevronDown"
          :class="{ 'rotate-180': open }"
          class="transition-all duration-500"
        />
      </ComboboxTrigger>
    </ComboboxAnchor>
    <ComboboxPortal>
      <Transition name="fade">
        <ComboboxContent :class="comboboxVariants.content.base()" position="popper" side="bottom" align="start">
          <ComboboxViewport>
            <ComboboxItem
              v-for="(option, index) in SORT_ORDER_OPTIONS"
              :key="index"
              :value="option"
              :class="comboboxVariants.content.item({ context: 'search' })"
            >
              {{ option.label }}
              <ComboboxItemIndicator>
                <Icon :icon="faCheck" class="text-primary" size="sm" />
              </ComboboxItemIndicator>
            </ComboboxItem>
          </ComboboxViewport>
        </ComboboxContent>
      </Transition>
    </ComboboxPortal>
  </ComboboxRoot>
</template>

<style scoped lang="postcss">
.fade-enter-active,
.fade-leave-active {
  @apply transition-opacity duration-300 ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
  @apply opacity-0;
}
</style>
